import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CustomSelect from "../../components/CustomSelect.jsx";
import PageHero from "../../components/PageHero.jsx";
import { fetchPublicRaces, fetchPublicResults, fetchPublicSeasons } from "../../lib/publicDbApi";
import { driverStatsPath } from "../../lib/driverStatsLinks";

const SESSION_OPTIONS = [
    { value: "RACE", label: "RACE" },
    { value: "QUALY", label: "QUALY" },
    { value: "PRACTICE", label: "PRACTICE" },
];

const CLASS_OPTIONS = [
    { value: "", label: "Gesamt" },
    { value: "PRO", label: "PRO" },
    { value: "INT", label: "INT" },
    { value: "AM", label: "AM" },
];

const PODIUM_STYLE = {
    1: { label: "Sieger", tone: "gold" },
    2: { label: "2", tone: "silver" },
    3: { label: "3", tone: "bronze" },
};

function isClassFiltered(classKey) {
    return ["PRO", "INT", "AM"].includes(String(classKey || "").toUpperCase());
}

function resultCarClass(row) {
    // Die im Rennergebnis gespeicherte Fahrerklasse ist maßgeblich.
    // So bleiben auch manuell geänderte Klassenzuordnungen korrekt.
    const value = String(row?.class_key || "").trim().toUpperCase();
    return ["PRO", "INT", "AM"].includes(value) ? value : "";
}

function CarNumberCell({ row }) {
    const number = String(row?.car_number_snapshot ?? "").trim();
    const driverClass = resultCarClass(row);

    return (
        <td className="rt-car-number-cell">
            {number ? (
                <span
                    className={`rt-car-number-badge${driverClass ? ` rt-car-number-badge--${driverClass.toLowerCase()}` : ""}`}
                    title={driverClass ? `Startnummer ${number} · ${driverClass}` : `Startnummer ${number}`}
                >
                    {number.startsWith("#") ? number : `#${number}`}
                </span>
            ) : (
                <span className="rt-dash">-</span>
            )}
        </td>
    );
}

function toInt(value) {
    const n = parseInt(String(value ?? "").trim(), 10);
    return Number.isFinite(n) ? n : null;
}

function displayFinalPos(row, classKey) {
    if (["DSQ", "DQ"].includes(String(row?.final_status || "").toUpperCase())) return null;
    if (isClassFiltered(classKey)) {
        return toInt(row?.view_final_position_class) ?? toInt(row?.final_position_class);
    }
    return toInt(row?.view_final_position_overall) ?? toInt(row?.final_position_overall);
}

function displayStartPos(row, classKey) {
    if (isClassFiltered(classKey)) {
        return (
            toInt(row?.view_start_position_class) ??
            toInt(row?.raw_start_position_class) ??
            toInt(row?.raw_start_position_overall)
        );
    }
    return toInt(row?.view_start_position_overall) ?? toInt(row?.raw_start_position_overall);
}

function deltaMeta(startPos, finalPos) {
    if (!Number.isFinite(startPos) || !Number.isFinite(finalPos)) {
        return { text: "-", cls: "delta-same" };
    }
    const gain = startPos - finalPos;
    if (gain > 0) return { text: `▲ ${gain}`, cls: "delta-up" };
    if (gain < 0) return { text: `▼ ${Math.abs(gain)}`, cls: "delta-down" };
    return { text: "-", cls: "delta-same" };
}

function gapDisplay(row, finalPos) {
    if (finalPos === 1) return "+0.000";
    const val = String(row?.final_gap_display || "").trim();
    return val || "-";
}

function intervalDisplay(row, finalPos) {
    if (finalPos === 1) return "-";
    const val = String(row?.final_interval_display || "").trim();
    return val || "-";
}

function formatPenaltySeconds(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0";
    return Number.isInteger(n) ? String(n) : String(n).replace(/\.?0+$/, "");
}

function rawFinishPos(row, classKey) {
    if (isClassFiltered(classKey)) {
        return toInt(row?.view_raw_position_class) ?? toInt(row?.raw_finish_position_class);
    }
    return toInt(row?.view_raw_position_overall) ?? toInt(row?.raw_finish_position_overall);
}

function stripPassivePositionLoss(summaryText) {
    const parts = String(summaryText || "")
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean)
        .filter((part) => {
            return !(
                /^positionsverlust\s*:\s*-\d+$/i.test(part) ||
                /^-\s*\d+\s+position(?:en)?(?:\s+verloren)?$/i.test(part)
            );
        });

    return parts.join("; ");
}

function creditSeconds(row) {
    const candidates = [
        row?.time_credit_seconds_total,
        row?.credit_seconds_total,
        row?.time_credit_seconds,
        row?.credited_seconds_total,
    ];

    for (const value of candidates) {
        const n = Number(value);
        if (Number.isFinite(n) && n > 0) return n;
    }

    const signedPenalty = Number(row?.penalty_seconds_total);
    if (Number.isFinite(signedPenalty) && signedPenalty < 0) {
        return Math.abs(signedPenalty);
    }

    return 0;
}

function adjustmentDisplay(row, classKey) {
    const sec = Number(row?.penalty_seconds_total);
    const classView = isClassFiltered(classKey);

    if (Number.isFinite(sec) && sec > 0) {
        const secText = formatPenaltySeconds(sec);

        const backendScopeDisplay = classView
            ? String(row?.penalty_display_class || "").trim()
            : String(row?.penalty_display_overall || "").trim();

        if (backendScopeDisplay) {
            return { text: backendScopeDisplay, tone: "penalty", gain: null };
        }

        const backendLoss = classView
            ? toInt(row?.penalty_positions_lost_class)
            : toInt(row?.penalty_positions_lost_overall);

        if (backendLoss !== null && backendLoss > 0) {
            return { text: `+${secText}s (-${backendLoss})`, tone: "penalty", gain: null };
        }

        const finalPos = displayFinalPos(row, classKey);
        const rawPos = rawFinishPos(row, classKey);

        if (rawPos !== null && finalPos !== null && finalPos > rawPos) {
            return { text: `+${secText}s (-${finalPos - rawPos})`, tone: "penalty", gain: null };
        }

        return { text: `+${secText}s`, tone: "penalty", gain: null };
    }

    const rawPos = rawFinishPos(row, classKey);
    const finalPos = displayFinalPos(row, classKey);
    const positionsGained =
        rawPos !== null && finalPos !== null && rawPos > finalPos
            ? rawPos - finalPos
            : 0;

    const cleanedSummary = stripPassivePositionLoss(row?.adjustment_summary);
    const creditSec = creditSeconds(row);
    const looksLikeCredit =
        creditSec > 0 ||
        /zeitgutschrift|gutschrift|time[_\s-]?credit/i.test(cleanedSummary) ||
        /^-\d+(?:[.,]\d+)?s\b/i.test(cleanedSummary);

    let text = cleanedSummary;
    if (!text && creditSec > 0) {
        text = `-${formatPenaltySeconds(creditSec)}s`;
    }

    return {
        text: text || "-",
        tone: looksLikeCredit ? "credit" : "neutral",
        gain: looksLikeCredit && positionsGained > 0 ? positionsGained : null,
    };
}

function pickFirstFilled(...values) {
    for (const value of values) {
        if (value === null || value === undefined) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return "";
}

function formatTicksToLapDisplay(value) {
    if (value === null || value === undefined) return "";
    const asNumber = Number(value);
    if (!Number.isFinite(asNumber)) return "";
    if (asNumber <= 0) return "";

    if (asNumber > 1000) {
        const totalSeconds = asNumber / 10000;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds - minutes * 60;
        return `${minutes}:${seconds.toFixed(3).padStart(6, "0")}`;
    }

    if (asNumber >= 60) {
        const minutes = Math.floor(asNumber / 60);
        const seconds = asNumber - minutes * 60;
        return `${minutes}:${seconds.toFixed(3).padStart(6, "0")}`;
    }

    return `0:${asNumber.toFixed(3).padStart(6, "0")}`;
}

function avgLapDisplay(row) {
    return (
        pickFirstFilled(
            row?.view_average_lap_display,
            row?.raw_average_lap_display,
            row?.average_lap_display,
            row?.avg_lap_display,
            formatTicksToLapDisplay(row?.raw_average_lap_ticks),
            formatTicksToLapDisplay(row?.average_lap_ticks),
            formatTicksToLapDisplay(row?.average_lap),
        ) || "-"
    );
}

function fastestLapDisplay(row) {
    return (
        pickFirstFilled(
            row?.view_best_lap_display,
            row?.raw_best_lap_display,
            row?.best_lap_display,
            row?.fastest_lap_display,
            formatTicksToLapDisplay(row?.raw_best_lap_ticks),
            formatTicksToLapDisplay(row?.best_lap_ticks),
            formatTicksToLapDisplay(row?.best_lap_time),
        ) || "No Time"
    );
}

function lapTicksFromRow(row) {
    const tickKeys = [
        "raw_best_lap_ticks",
        "best_lap_ticks",
        "best_lap_time",
    ];
    for (const key of tickKeys) {
        const n = Number(row?.[key]);
        if (Number.isFinite(n) && n > 0) {
            return n > 1000 ? Math.round(n) : Math.round(n * 10000);
        }
    }

    const text = fastestLapDisplay(row);
    if (!text || text === "-") return null;
    const clean = String(text).trim().replace("+", "").replace(",", ".");
    if (!clean) return null;

    const parts = clean.split(":");
    let seconds = null;
    if (parts.length === 2) {
        const min = Number(parts[0]);
        const sec = Number(parts[1]);
        if (Number.isFinite(min) && Number.isFinite(sec)) seconds = min * 60 + sec;
    } else if (parts.length === 1) {
        const sec = Number(parts[0]);
        if (Number.isFinite(sec)) seconds = sec;
    }
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    return Math.round(seconds * 10000);
}

function qualyPracticeGapDisplay(row, finalPos) {
    if (fastestLapDisplay(row) === "No Time") return "-";
    if (finalPos === 1) return "+0.000";
    return pickFirstFilled(row?.final_gap_display, row?.raw_gap_display) || "-";
}

function initials(name) {
    const s = String(name || "").trim();
    if (!s) return "?";
    return s
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
}

function scoreValue(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "–";
    return n.toFixed(1).replace(/\.0$/, "");
}

function performanceExplanation(performance) {
    const explanation = performance?.explanation_json;
    if (explanation && typeof explanation === "object") {
        return String(explanation.summary || "").trim();
    }
    return "Leistungsbilanz wurde berechnet, aber es liegt noch keine Begründung vor.";
}

function performanceStrengths(performance) {
    const explanation = performance?.explanation_json;
    if (!explanation || typeof explanation !== "object" || !Array.isArray(explanation.strengths)) {
        return [];
    }
    return explanation.strengths.filter(Boolean).slice(0, 4);
}


function PerformanceScoreInfo() {
    return (
        <span className="rv-score-info">
            <button type="button" className="rv-score-info__button" aria-label="Info zur Driver-of-the-Day-Bewertung">
                i
            </button>
            <span className="rv-score-info__popup" role="tooltip">
                <strong>Driver-of-the-Day-Score</strong>
                <span>Gesamt = Ergebnis 25%, Progress 20%, Pace 20%, Qualy 15%, Clean 10%, Konstanz 10%.</span>
                <span>Ergebnis bewertet vor allem das Klassenresultat. Progress bewertet Start → Ziel fair zur möglichen Verbesserung.</span>
                <span>Pace nutzt die Average Lap gegen den Klassenbestwert. Clean berücksichtigt Incidents und Strafen. Konstanz ist aktuell Avg Lap vs Best Lap.</span>
                <span>Bonus bis +5 für besondere Leistungen, z. B. Klassensieg nach schwieriger Qualy oder starke Aufholjagd.</span>
            </span>
        </span>
    );
}

function RaceSourceBadge({ flagUrl, trackName, raceTag }) {
    const track = String(trackName || "").trim();
    const tag = String(raceTag || "").trim();

    if (!track && !tag) return null;

    return (
        <span className="rv-event-source" title={[tag, track].filter(Boolean).join(" · ")}>
            {flagUrl ? <img src={flagUrl} alt="Rennen" loading="lazy" /> : <span className="rv-event-source__flagFallback">🏁</span>}
            <span className="rv-event-source__text">{[tag, track].filter(Boolean).join(" · ")}</span>
        </span>
    );
}

function DriverOfTheDayCard({ performance, seasonKey }) {
    if (!performance) return null;

    const driverName = String(performance?.driver_name_snapshot || "Driver of the Day");
    const driverImage = String(
        performance?.driver_image_url_resolved || performance?.driver_image_url_snapshot || performance?.discord_avatar_url || "",
    ).trim();
    const carLogo = String(performance?.brand_logo_url_snapshot || "").trim();
    const teamLogo = String(performance?.team_logo_url_snapshot || "").trim();
    const teamName = String(performance?.team_name_snapshot || "").trim();
    const strengths = performanceStrengths(performance);

    const raceTrackName = String(performance?.performance_track_name || performance?.track_name || "").trim();
    const raceFlagUrl = String(performance?.performance_flag_url || performance?.event_flag_url || "").trim();
    const raceTag = String(performance?.performance_race_tag || performance?.race_tag || "").trim();

    const breakdown = [
        { label: "Ergebnis", value: performance?.result_score },
        { label: "Progress", value: performance?.progress_score },
        { label: "Pace", value: performance?.pace_score },
        { label: "Qualy", value: performance?.quali_score },
        { label: "Clean", value: performance?.cleanliness_score },
        { label: "Konstanz", value: performance?.consistency_score },
    ];

    const start = toInt(performance?.explanation_json?.metrics?.start_all_pos);
    const finish = toInt(performance?.explanation_json?.metrics?.race_pos);
    const classPos = toInt(performance?.explanation_json?.metrics?.class_pos);
    const incidents = toInt(performance?.incidents);

    return (
        <section className="rv-dotd-card rv-dotd-card--compact">
            <div className="rv-dotd-card__topline">
                <div className="rv-dotd-card__kicker">Driver of the Day</div>
                <div className="rv-dotd-card__tools">
                    <RaceSourceBadge flagUrl={raceFlagUrl} trackName={raceTrackName} raceTag={raceTag} />
                    <PerformanceScoreInfo />
                </div>
            </div>

            <div className="rv-dotd-card__main">
                <div className="rv-dotd-card__avatarWrap">
                    <div className="rv-dotd-card__avatarStack">
                        {driverImage ? (
                            <img src={driverImage} alt={driverName} className="rv-dotd-card__avatar" loading="lazy" />
                        ) : (
                            <div className="rv-dotd-card__avatar rv-dotd-card__avatarFallback">{initials(driverName)}</div>
                        )}

                        {teamLogo ? (
                            <div className="rv-dotd-card__avatarTeamLogo">
                                <img src={teamLogo} alt="Team" loading="lazy" />
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="rv-dotd-card__content">
                    <div className="rv-dotd-card__titleRow">
                        <div className="rv-dotd-card__titleNameLine">
                            <h2>
                                {performance?.driver_id ? (
                                    <Link to={driverStatsPath(performance, seasonKey)} className="rv-driver-link">
                                        {driverName}
                                    </Link>
                                ) : (
                                    driverName
                                )}
                            </h2>

                            {carLogo ? (
                                <div className="rv-dotd-card__inlineLogos">
                                    <img src={carLogo} alt="Auto" loading="lazy" />
                                </div>
                            ) : null}
                        </div>

                        <div className="rv-dotd-card__score">{scoreValue(performance?.dotd_score)}</div>
                    </div>

                    <div className="rv-dotd-card__meta">
                        <span>#{performance?.car_number_snapshot || "–"}</span>
                        <span>{performance?.class_key || "Klasse"}</span>
                        {start && finish ? <span>P{start} → P{finish} Overall</span> : null}
                        {classPos ? <span>P{classPos} Klasse</span> : null}
                        {incidents !== null ? <span>{incidents}x</span> : null}
                        {teamName ? <span>{teamName}</span> : null}
                    </div>

                    {!!strengths.length && (
                        <div className="rv-dotd-card__strengths">
                            {strengths.map((item) => (
                                <span key={item}>{item}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="rv-dotd-breakdown">
                {breakdown.map((item) => (
                    <div key={item.label} className="rv-dotd-breakdown__item">
                        <span>{item.label}</span>
                        <strong>{scoreValue(item.value)}</strong>
                    </div>
                ))}
                {Number(performance?.bonus_score || 0) > 0 && (
                    <div className="rv-dotd-breakdown__item rv-dotd-breakdown__item--bonus">
                        <span>Bonus</span>
                        <strong>+{scoreValue(performance?.bonus_score)}</strong>
                    </div>
                )}
            </div>
        </section>
    );
}

function PodiumCard({ row, rank, classKey, seasonKey }) {
    const pos = displayFinalPos(row, classKey);
    const pod = PODIUM_STYLE[rank] || PODIUM_STYLE[3];
    const driverName = String(row?.driver_name_snapshot || "Unknown Driver");
    const driverImage = String(
        row?.driver_image_url_resolved || row?.driver_image_url_snapshot || row?.discord_avatar_url || "",
    ).trim();
    const carLogo = String(row?.brand_logo_url_snapshot || "").trim();
    const teamLogo = String(row?.team_logo_url_snapshot || "").trim();

    return (
        <article className={`rt-podium-card rt-podium-card--${pod.tone}`}>
            <div className="rt-podium-card__head">
                <div className="rt-podium-rank">{pos ?? rank}</div>
                {rank === 1 && <div className="rt-podium-badge">{pod.label}</div>}
            </div>

            <div className="rt-podium-driver">
                <div className="rt-podium-avatar-wrap">
                    {driverImage ? (
                        <img src={driverImage} alt={driverName} className="rt-podium-avatar" loading="lazy" />
                    ) : (
                        <div className="rt-podium-avatar-fallback">{initials(driverName)}</div>
                    )}
                </div>

                <div className="rt-podium-driver-meta">
                    <h3>
                        {row?.driver_id ? (
                            <Link
                                to={driverStatsPath(row, seasonKey)}
                                className="rv-driver-link"
                            >
                                {driverName}
                            </Link>
                        ) : (
                            driverName
                        )}
                    </h3>
                    <div className="rt-podium-subline">#{row?.car_number_snapshot || "-"}</div>
                    <div className="rt-podium-logos">
                        {carLogo ? <img src={carLogo} alt="Auto" loading="lazy" /> : <span className="rt-podium-logo-fallback">AUTO</span>}
                        {teamLogo ? <img src={teamLogo} alt="Team" loading="lazy" /> : <span className="rt-podium-logo-fallback">TEAM</span>}
                    </div>
                    <div className="rt-podium-team">{row?.team_name_snapshot || "-"}</div>
                </div>
            </div>

            <div className="rt-podium-foot">
                <span>GAP</span>
                <strong>{gapDisplay(row, pos)}</strong>
            </div>
        </article>
    );
}

function parseRaceDateMs(value) {
    if (!value) return null;

    const raw = String(value).trim();
    if (!raw) return null;

    const date = new Date(raw);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.getTime();
}

function raceOrderValue(race) {
    return (
        toInt(race?.sort_order) ??
        toInt(race?.race_no) ??
        999999
    );
}

function raceTagValue(race) {
    return String(race?.race_tag || "").trim();
}

function pickDefaultResultsRaceTag(races) {
    const list = Array.isArray(races) ? races.filter((race) => raceTagValue(race)) : [];

    if (!list.length) return "";

    const now = Date.now();

    /*
        1. Für Results am wichtigsten:
        Nimm das neueste Rennen, dessen Results sichtbar sind.
    */
    const visibleResults = list
        .map((race) => ({
            race,
            ms: parseRaceDateMs(race?.results_from),
        }))
        .filter((entry) => entry.ms !== null && entry.ms <= now)
        .sort((a, b) => b.ms - a.ms || raceOrderValue(b.race) - raceOrderValue(a.race));

    if (visibleResults.length) {
        return raceTagValue(visibleResults[0].race);
    }

    /*
        2. Falls noch keine Results sichtbar sind:
        Nimm das aktuelle Rennwochenende laut Saisonverwaltung.
        briefing_from <= jetzt < results_from
    */
    const currentWindow = list.find((race) => {
        const briefingFrom = parseRaceDateMs(race?.briefing_from);
        const resultsFrom = parseRaceDateMs(race?.results_from);

        if (briefingFrom === null) return false;
        if (briefingFrom > now) return false;
        if (resultsFrom !== null && resultsFrom <= now) return false;

        return true;
    });

    if (currentWindow) {
        return raceTagValue(currentWindow);
    }

    /*
        3. Sonst nächstes geplantes Rennen.
    */
    const upcoming = list
        .map((race) => ({
            race,
            ms:
                parseRaceDateMs(race?.briefing_from) ??
                parseRaceDateMs(race?.race_date),
        }))
        .filter((entry) => entry.ms !== null && entry.ms > now)
        .sort((a, b) => a.ms - b.ms || raceOrderValue(a.race) - raceOrderValue(b.race));

    if (upcoming.length) {
        return raceTagValue(upcoming[0].race);
    }

    /*
        4. Fallback:
        Letztes Rennen nach Sortierung/RaceNo.
    */
    const sorted = [...list].sort((a, b) => raceOrderValue(b) - raceOrderValue(a));
    return raceTagValue(sorted[0]);
}

export default function Results({ seasonKey = "active" }) {
    const [races, setRaces] = useState([]);
    const [raceTag, setRaceTag] = useState("");
    const [resolvedSeasonKey, setResolvedSeasonKey] = useState(seasonKey);
    const [sessionType, setSessionType] = useState("RACE");
    const [classKey, setClassKey] = useState("");
    const [rows, setRows] = useState([]);
    const [driverOfTheDay, setDriverOfTheDay] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const showCarNumbers = !isClassFiltered(classKey);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setError("");
            try {
                let nextSeasonKey = String(seasonKey || "active");
                let data;
                try {
                    data = await fetchPublicRaces("gt3x", nextSeasonKey);
                } catch (err) {
                    const msg = String(err?.message || err || "");
                    const shouldFallback =
                        msg.includes("Season not found") ||
                        msg.includes("HTTP 404");

                    const requestedSeasonKey = String(seasonKey || "active").trim().toLowerCase();
                    const allowFallback = !requestedSeasonKey || requestedSeasonKey === "active";

                    if (!shouldFallback || !allowFallback) {
                        throw err;
                    }

                    const seasonsData = await fetchPublicSeasons("gt3x");
                    const seasons = Array.isArray(seasonsData?.seasons) ? seasonsData.seasons : [];
                    const activeSeason = seasons.find((s) => String(s?.status || "").toLowerCase() === "active");
                    const fallbackSeason = activeSeason?.season_key || seasons[0]?.season_key;
                    if (!fallbackSeason) throw err;

                    nextSeasonKey = String(fallbackSeason);
                    data = await fetchPublicRaces("gt3x", nextSeasonKey);
                }

                const list = Array.isArray(data?.races) ? data.races : [];
                const defaultRaceTag = pickDefaultResultsRaceTag(list);

                setResolvedSeasonKey(nextSeasonKey);
                setRaces(list);
                setRaceTag(defaultRaceTag);
            } catch (err) {
                setError(err?.message || String(err));
                setRaces([]);
                setRaceTag("");
            } finally {
                setLoading(false);
            }
        })();
    }, [seasonKey]);

    useEffect(() => {
        (async () => {
            if (!raceTag) return;
            setLoading(true);
            setError("");
            try {
                const data = await fetchPublicResults({
                    seriesKey: "gt3x",
                    seasonKey: resolvedSeasonKey,
                    raceTag,
                    sessionType,
                    classKey,
                });
                setRows(Array.isArray(data?.rows) ? data.rows : []);
                setDriverOfTheDay(data?.driver_of_the_day || null);
            } catch (err) {
                setError(err?.message || String(err));
                setRows([]);
                setDriverOfTheDay(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [raceTag, sessionType, classKey, resolvedSeasonKey]);

    const raceOptions = useMemo(
        () =>
            races.map((r) => ({
                value: String(r?.race_tag || ""),
                label: `${r?.race_tag || "-"} - ${r?.track_name || "Track"}`,
            })),
        [races],
    );

    const sortedRows = useMemo(() => {
        const list = [...rows];
        list.sort((a, b) => {
            const aPos = displayFinalPos(a, classKey) ?? 999999;
            const bPos = displayFinalPos(b, classKey) ?? 999999;
            if (aPos !== bPos) return aPos - bPos;
            return (toInt(a?.final_position_overall) ?? 999999) - (toInt(b?.final_position_overall) ?? 999999);
        });
        return list;
    }, [rows, classKey]);

    const podiumRows = useMemo(() => {
        if (sessionType !== "RACE") return [];
        return sortedRows.filter((row) => displayFinalPos(row, classKey) !== null).slice(0, 3);
    }, [sessionType, sortedRows, classKey]);

    const podiumByRank = useMemo(
        () => ({
            1: podiumRows.find((r) => displayFinalPos(r, classKey) === 1) || podiumRows[0] || null,
            2: podiumRows.find((r) => displayFinalPos(r, classKey) === 2) || podiumRows[1] || null,
            3: podiumRows.find((r) => displayFinalPos(r, classKey) === 3) || podiumRows[2] || null,
        }),
        [podiumRows, classKey],
    );

    const fastestRaceLapBest = useMemo(() => {
        if (sessionType !== "RACE") return null;
        let best = null;
        for (const row of sortedRows) {
            const ticks = lapTicksFromRow(row);
            if (ticks === null) continue;
            if (best === null || ticks < best) best = ticks;
        }
        return best;
    }, [sessionType, sortedRows]);

    return (
        <div className="rv-page rv-results-testing">
            <PageHero kicker="GT3 Xperience" title="Rennergebnisse">
                {sessionType} {classKey || "GESAMT"} · Saison {resolvedSeasonKey || "-"}
            </PageHero>

            <section className="rv-modern-card rv-filter-card">
                <div className="rt-filters">
                    <label className="rv-dd rt-filter-item">
                        <span className="rv-dd__label">Rennen</span>
                        <CustomSelect
                            value={raceTag}
                            onChange={(value) => setRaceTag(String(value || ""))}
                            allowEmpty={false}
                            buttonClassName="rv-select--race"
                            options={raceOptions}
                        />
                    </label>

                    <label className="rv-dd rt-filter-item">
                        <span className="rv-dd__label">Session</span>
                        <CustomSelect
                            value={sessionType}
                            onChange={(value) => setSessionType(String(value || "RACE"))}
                            allowEmpty={false}
                            buttonClassName={`rv-select--${String(sessionType || "RACE").toLowerCase()}`}
                            options={SESSION_OPTIONS}
                        />
                    </label>

                    <label className="rv-dd rt-filter-item">
                        <span className="rv-dd__label">Klasse</span>
                        <CustomSelect
                            value={classKey}
                            onChange={(value) => setClassKey(String(value || ""))}
                            allowEmpty={false}
                            buttonClassName={`rv-select--${String(classKey || "GESAMT").toLowerCase()}`}
                            options={CLASS_OPTIONS}
                        />
                    </label>
                </div>
            </section>

            {!!error && (
                <div className="rv-modern-card">
                    <p className="rv-message rv-message--error" style={{ margin: 0 }}>
                        Fehler: {error}
                    </p>
                </div>
            )}

            {sessionType === "RACE" && !loading && !error && driverOfTheDay && (
                <DriverOfTheDayCard performance={driverOfTheDay} seasonKey={resolvedSeasonKey} />
            )}

            {sessionType === "RACE" && !loading && !error && podiumRows.length > 0 && (
                <section className="rt-podium-grid">
                    {podiumByRank[2] && <PodiumCard row={podiumByRank[2]} rank={2} classKey={classKey} seasonKey={resolvedSeasonKey} />}
                    {podiumByRank[1] && <PodiumCard row={podiumByRank[1]} rank={1} classKey={classKey} seasonKey={resolvedSeasonKey} />}
                    {podiumByRank[3] && <PodiumCard row={podiumByRank[3]} rank={3} classKey={classKey} seasonKey={resolvedSeasonKey} />}
                </section>
            )}

            <section className="rt-table-wrap">
                <table className="rt-table">
                    <thead>
                    {sessionType === "RACE" ? (
                        <tr>
                            <th>Pos</th>
                            <th>Start</th>
                            <th>+/-</th>
                            <th className="rt-sep-col" aria-hidden="true"></th>
                            <th></th>
                            <th>Fahrername</th>
                            {showCarNumbers && <th className="rt-car-number-head">#</th>}
                            <th></th>
                            <th></th>
                            <th>Teamname</th>
                            <th>GAP</th>
                            <th>Interval</th>
                            <th>Zeitstrafe</th>
                            <th>Offtracks</th>
                            <th>AVG Laptime</th>
                            <th>Fastest Laptime</th>
                        </tr>
                    ) : (
                        <tr>
                            <th>Pos</th>
                            <th></th>
                            <th>Fahrername</th>
                            {showCarNumbers && <th className="rt-car-number-head">#</th>}
                            <th></th>
                            <th></th>
                            <th>Teamname</th>
                            <th>GAP</th>
                            <th>Fastest Laptime</th>
                        </tr>
                    )}
                    </thead>

                    <tbody>
                    {loading && (
                        <tr>
                            <td colSpan={(sessionType === "RACE" ? 15 : 8) + (showCarNumbers ? 1 : 0)} className="rt-empty">
                                Lade...
                            </td>
                        </tr>
                    )}

                    {!loading && !error && !sortedRows.length && (
                        <tr>
                            <td colSpan={(sessionType === "RACE" ? 15 : 8) + (showCarNumbers ? 1 : 0)} className="rt-empty">
                                Keine Daten.
                            </td>
                        </tr>
                    )}

                    {!loading &&
                        !error &&
                        sortedRows.map((row) => {
                            const finalPos = displayFinalPos(row, classKey);
                            const startPos = displayStartPos(row, classKey);
                            const delta = deltaMeta(startPos, finalPos);
                            const rowFastestTicks = lapTicksFromRow(row);

                            return (
                                <tr key={row?.id ?? `${row?.driver_name_snapshot}-${row?.car_number_snapshot}`}>
                                    {sessionType === "RACE" ? (
                                        <>
                                            <td className="rt-num">{finalPos ?? (["DSQ", "DQ"].includes(String(row?.final_status || "").toUpperCase()) ? String(row.final_status).toUpperCase() : "-")}</td>
                                            <td className="rt-num">{startPos ?? "-"}</td>
                                            <td className={`rt-num ${delta.cls}`}>{delta.text}</td>
                                            <td className="rt-sep-col" aria-hidden="true"></td>
                                            <td className="rt-icon">
                                                {row?.flag_url_snapshot ? (
                                                    <img src={row.flag_url_snapshot} alt={row?.country_code_snapshot || "Flag"} className="rt-flag" loading="lazy" />
                                                ) : (
                                                    <span className="rt-dash">-</span>
                                                )}
                                            </td>
                                            <td className="rt-driver">
                                                {row?.driver_id ? (
                                                    <Link
                                                        to={driverStatsPath(row, resolvedSeasonKey)}
                                                        className="rv-driver-link"
                                                    >
                                                        {row?.driver_name_snapshot || "-"}
                                                    </Link>
                                                ) : (
                                                    row?.driver_name_snapshot || "-"
                                                )}
                                            </td>
                                            {showCarNumbers && <CarNumberCell row={row} />}
                                            <td className="rt-icon">
                                                {row?.brand_logo_url_snapshot ? (
                                                    <img src={row.brand_logo_url_snapshot} alt="Auto" className="rt-brand-logo" loading="lazy" />
                                                ) : (
                                                    <span className="rt-dash">-</span>
                                                )}
                                            </td>
                                            <td className="rt-icon">
                                                {row?.team_logo_url_snapshot ? (
                                                    <img src={row.team_logo_url_snapshot} alt="Team" className="rt-team-logo" loading="lazy" />
                                                ) : (
                                                    <span className="rt-dash">-</span>
                                                )}
                                            </td>
                                            <td>{row?.team_name_snapshot || "-"}</td>
                                            <td className="rt-time">{gapDisplay(row, finalPos)}</td>
                                            <td className="rt-time">{intervalDisplay(row, finalPos)}</td>
                                            {(() => {
                                                const adjustment = adjustmentDisplay(row, classKey);
                                                return (
                                                    <td className={adjustment.tone === "penalty" ? "rt-penalty" : ""}>
                                                        {adjustment.text}
                                                        {adjustment.gain ? (
                                                            <span className="rt-credit-gain"> (+{adjustment.gain})</span>
                                                        ) : null}
                                                    </td>
                                                );
                                            })()}
                                            <td className="rt-num">
                                                {row?.offtrack_count === null || row?.offtrack_count === undefined
                                                    ? "-"
                                                    : Number(row.offtrack_count)}
                                            </td>
                                            <td className="rt-time">{avgLapDisplay(row)}</td>
                                            <td
                                                className={`rt-time ${
                                                    fastestRaceLapBest !== null &&
                                                    rowFastestTicks !== null &&
                                                    rowFastestTicks === fastestRaceLapBest
                                                        ? "rt-fastest-best"
                                                        : ""
                                                }`.trim()}
                                            >
                                                {fastestLapDisplay(row)}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="rt-num">{finalPos ?? "-"}</td>
                                            <td className="rt-icon">
                                                {row?.flag_url_snapshot ? (
                                                    <img src={row.flag_url_snapshot} alt={row?.country_code_snapshot || "Flag"} className="rt-flag" loading="lazy" />
                                                ) : (
                                                    <span className="rt-dash">-</span>
                                                )}
                                            </td>
                                            <td className="rt-driver">
                                                {row?.driver_id ? (
                                                    <Link
                                                        to={driverStatsPath(row, resolvedSeasonKey)}
                                                        className="rv-driver-link"
                                                    >
                                                        {row?.driver_name_snapshot || "-"}
                                                    </Link>
                                                ) : (
                                                    row?.driver_name_snapshot || "-"
                                                )}
                                            </td>
                                            {showCarNumbers && <CarNumberCell row={row} />}
                                            <td className="rt-icon">
                                                {row?.brand_logo_url_snapshot ? (
                                                    <img src={row.brand_logo_url_snapshot} alt="Auto" className="rt-brand-logo" loading="lazy" />
                                                ) : (
                                                    <span className="rt-dash">-</span>
                                                )}
                                            </td>
                                            <td className="rt-icon">
                                                {row?.team_logo_url_snapshot ? (
                                                    <img src={row.team_logo_url_snapshot} alt="Team" className="rt-team-logo" loading="lazy" />
                                                ) : (
                                                    <span className="rt-dash">-</span>
                                                )}
                                            </td>
                                            <td>{row?.team_name_snapshot || "-"}</td>
                                            <td className="rt-time">{qualyPracticeGapDisplay(row, finalPos)}</td>
                                            <td className="rt-time">{fastestLapDisplay(row)}</td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>
        </div>
    );
}


